import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  ArrowLeft, Activity, TrendingUp, TrendingDown, AlertTriangle, Zap, 
  Clock, Settings2, Filter, ChevronDown, ChevronUp, Save, RefreshCw,
  Calendar, Download, Trash2, Database, Plus, X, ExternalLink, Sliders,
  Send, Bell, BellOff
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Activity types
const ACTIVITY_TYPES = {
  HIGH_LTQ_STREAK: 'high_ltq_streak',
  SIGNIFICANT_BUY_QTY: 'significant_buy_qty',
  SIGNIFICANT_SELL_QTY: 'significant_sell_qty',
  AGGRESSIVE_BUY_STREAK: 'aggressive_buy_streak',
  AGGRESSIVE_SELL_STREAK: 'aggressive_sell_streak',
  INSTITUTIONAL_BUY: 'institutional_buy',
  INSTITUTIONAL_SELL: 'institutional_sell',
  ICEBERG_ORDER: 'iceberg_order',
  VOLUME_SPIKE: 'volume_spike',
  OI_SPIKE: 'oi_spike',
  PRICE_BREAKOUT: 'price_breakout',
  BID_ASK_IMBALANCE: 'bid_ask_imbalance',
  LARGE_TRADE: 'large_trade',
  CUSTOM_COMBINED: 'custom_combined',
};

// Condition types for custom combined filters
const CONDITION_TYPES = {
  BID_QTY_PERCENT: { key: 'bid_qty_percent', label: 'Bid Qty %', operator: '>', defaultValue: 50 },
  ASK_QTY_PERCENT: { key: 'ask_qty_percent', label: 'Ask Qty %', operator: '>', defaultValue: 50 },
  LTQ_GREATER: { key: 'ltq_greater', label: 'LTQ >', operator: '>', defaultValue: 500 },
  LTQ_LESS: { key: 'ltq_less', label: 'LTQ <', operator: '<', defaultValue: 100 },
  VOLUME_SPIKE: { key: 'volume_spike', label: 'Volume Spike', operator: 'x', defaultValue: 2 },
  PRICE_CHANGE: { key: 'price_change', label: 'Price Change %', operator: '>', defaultValue: 0.5 },
  AGG_BUY: { key: 'agg_buy', label: 'Aggressive Buy', operator: 'bool', defaultValue: true },
  AGG_SELL: { key: 'agg_sell', label: 'Aggressive Sell', operator: 'bool', defaultValue: true },
  OI_CHANGE: { key: 'oi_change', label: 'OI Change %', operator: '>', defaultValue: 1 },
};

// Default activity settings (customizable thresholds)
const DEFAULT_ACTIVITY_SETTINGS = {
  [ACTIVITY_TYPES.HIGH_LTQ_STREAK]: { enabled: true, ltqThreshold: 1400, consecutiveTicks: 5, label: 'High LTQ Streak' },
  [ACTIVITY_TYPES.SIGNIFICANT_BUY_QTY]: { enabled: true, changePercent: 20, label: 'Significant Buy Qty' },
  [ACTIVITY_TYPES.SIGNIFICANT_SELL_QTY]: { enabled: true, changePercent: 20, label: 'Significant Sell Qty' },
  [ACTIVITY_TYPES.AGGRESSIVE_BUY_STREAK]: { enabled: true, streakCount: 4, label: 'Aggressive Buy Streak' },
  [ACTIVITY_TYPES.AGGRESSIVE_SELL_STREAK]: { enabled: true, streakCount: 4, label: 'Aggressive Sell Streak' },
  [ACTIVITY_TYPES.INSTITUTIONAL_BUY]: { enabled: true, ltqThreshold: 500, volumeMultiplier: 2, label: 'Institutional Buy' },
  [ACTIVITY_TYPES.INSTITUTIONAL_SELL]: { enabled: true, ltqThreshold: 500, volumeMultiplier: 2, label: 'Institutional Sell' },
  [ACTIVITY_TYPES.ICEBERG_ORDER]: { enabled: true, maxLtq: 50, volumeMultiplier: 3, label: 'Iceberg Order' },
  [ACTIVITY_TYPES.VOLUME_SPIKE]: { enabled: true, volumeMultiplier: 3, label: 'Volume Spike' },
  [ACTIVITY_TYPES.OI_SPIKE]: { enabled: true, oiChangePercent: 5, label: 'OI Spike' },
  [ACTIVITY_TYPES.PRICE_BREAKOUT]: { enabled: true, breakoutPercent: 1, label: 'Price Breakout' },
  [ACTIVITY_TYPES.BID_ASK_IMBALANCE]: { enabled: true, imbalancePercent: 70, label: 'Bid/Ask Imbalance' },
  [ACTIVITY_TYPES.LARGE_TRADE]: { enabled: true, ltqThreshold: 1000, label: 'Large Trade' },
};

// Default custom filters
const DEFAULT_CUSTOM_FILTERS = [];

const formatTime = (timestamp) => {
  if (!timestamp) return "-";
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatVolume = (num) => {
  if (!num) return "-";
  if (num >= 10000000) return (num / 10000000).toFixed(2) + " Cr";
  if (num >= 100000) return (num / 100000).toFixed(2) + " L";
  if (num >= 1000) return (num / 1000).toFixed(1) + " K";
  return num.toLocaleString('en-IN');
};

const ActivityIcon = ({ type }) => {
  if (type.includes('buy') || type.includes('BUY')) return <TrendingUp className="w-4 h-4 text-green-400" />;
  if (type.includes('sell') || type.includes('SELL')) return <TrendingDown className="w-4 h-4 text-red-400" />;
  if (type.includes('iceberg')) return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
  if (type.includes('ltq') || type.includes('LTQ') || type.includes('custom')) return <Zap className="w-4 h-4 text-purple-400" />;
  return <Activity className="w-4 h-4 text-blue-400" />;
};

const getActivityColor = (type) => {
  if (type.includes('buy') || type.includes('BUY')) return 'border-green-500/30 bg-gradient-to-r from-green-900/20 to-transparent';
  if (type.includes('sell') || type.includes('SELL')) return 'border-red-500/30 bg-gradient-to-r from-red-900/20 to-transparent';
  if (type.includes('iceberg')) return 'border-yellow-500/30 bg-gradient-to-r from-yellow-900/20 to-transparent';
  if (type.includes('ltq') || type.includes('LTQ') || type.includes('custom')) return 'border-purple-500/30 bg-gradient-to-r from-purple-900/20 to-transparent';
  return 'border-blue-500/30 bg-gradient-to-r from-blue-900/20 to-transparent';
};

const getActivityLabel = (type, settings, customFilters) => {
  if (type.startsWith('custom_')) {
    const filterId = type.replace('custom_', '');
    const filter = customFilters.find(f => f.id === filterId);
    return filter?.name || 'Custom Filter';
  }
  return settings[type]?.label || type.replace(/_/g, ' ').toUpperCase();
};

// Custom Combined Filter Editor Component
const CustomFilterEditor = ({ filter, onChange, onDelete, availableSymbols = [] }) => {
  const [isEditing, setIsEditing] = useState(!filter.name);
  const [showSymbolPicker, setShowSymbolPicker] = useState(false);
  const [symbolSearch, setSymbolSearch] = useState('');
  
  const addCondition = () => {
    const newCondition = {
      id: Date.now().toString(),
      type: 'bid_qty_percent',
      value: 50,
      operator: '>'
    };
    onChange({ ...filter, conditions: [...filter.conditions, newCondition] });
  };
  
  const updateCondition = (condId, updates) => {
    onChange({
      ...filter,
      conditions: filter.conditions.map(c => c.id === condId ? { ...c, ...updates } : c)
    });
  };
  
  const deleteCondition = (condId) => {
    onChange({
      ...filter,
      conditions: filter.conditions.filter(c => c.id !== condId)
    });
  };
  
  const toggleSymbol = (symbol) => {
    const currentSymbols = filter.symbols || [];
    if (currentSymbols.includes(symbol)) {
      onChange({ ...filter, symbols: currentSymbols.filter(s => s !== symbol) });
    } else {
      onChange({ ...filter, symbols: [...currentSymbols, symbol] });
    }
  };
  
  const filteredSymbols = availableSymbols.filter(s => 
    s.toLowerCase().includes(symbolSearch.toLowerCase())
  );
  
  return (
    <div className="p-3 bg-[#0A0A0A] rounded-lg border border-[#27272A] mb-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-1">
          {isEditing ? (
            <Input
              value={filter.name}
              onChange={(e) => onChange({ ...filter, name: e.target.value })}
              placeholder="Filter Name (e.g., Big Buy Signal)"
              className="h-8 text-sm bg-[#1a1a1a] border-[#27272A] flex-1"
            />
          ) : (
            <span className="font-bold text-white">{filter.name || 'Unnamed Filter'}</span>
          )}
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={filter.enabled} 
              onChange={(e) => onChange({ ...filter, enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-[#27272A] peer-checked:bg-purple-600 rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
          </label>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => setIsEditing(!isEditing)} className="h-7 px-2">
            <Settings2 className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} className="h-7 px-2 text-red-400 hover:text-red-300">
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>
      
      {/* Trigger Mode Selection */}
      <div className="flex items-center gap-3 mb-3 p-2 bg-[#1a1a1a] rounded border border-[#27272A]">
        <span className="text-xs text-[#71717A]">Trigger Mode:</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input 
            type="radio" 
            name={`mode-${filter.id}`}
            checked={filter.triggerMode === 'consecutive' || !filter.triggerMode}
            onChange={() => onChange({ ...filter, triggerMode: 'consecutive' })}
            className="accent-purple-500"
          />
          <span className="text-xs text-white">Consecutive Ticks</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input 
            type="radio" 
            name={`mode-${filter.id}`}
            checked={filter.triggerMode === 'within'}
            onChange={() => onChange({ ...filter, triggerMode: 'within' })}
            className="accent-purple-500"
          />
          <span className="text-xs text-white">Within X Ticks (any order)</span>
        </label>
      </div>
      
      {/* Tick Count Setting */}
      <div className="flex items-center gap-2 mb-3 text-xs">
        <span className="text-[#71717A]">
          {filter.triggerMode === 'within' 
            ? 'Trigger when ALL conditions match within' 
            : 'Trigger when ALL conditions match in'}
        </span>
        <Input
          type="number"
          value={filter.tickCount || 3}
          onChange={(e) => onChange({ ...filter, tickCount: parseInt(e.target.value) || 1 })}
          className="h-6 w-14 text-xs bg-[#1a1a1a] border-[#27272A]"
          min={1}
          max={50}
        />
        <span className="text-[#71717A]">
          {filter.triggerMode === 'within' ? 'ticks (any order)' : 'consecutive ticks'}
        </span>
      </div>
      
      {/* Symbol Filter */}
      <div className="mb-3 p-2 bg-[#1a1a1a] rounded border border-[#27272A]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#71717A]">Apply to Symbols:</span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowSymbolPicker(!showSymbolPicker)}
            className="h-6 text-xs text-purple-400"
          >
            {showSymbolPicker ? 'Hide' : 'Select Symbols'}
          </Button>
        </div>
        
        {(!filter.symbols || filter.symbols.length === 0) ? (
          <span className="text-xs text-green-400">All Symbols (no filter)</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {filter.symbols.map(sym => (
              <span 
                key={sym} 
                className="px-2 py-0.5 bg-purple-600/30 text-purple-300 text-xs rounded flex items-center gap-1"
              >
                {sym}
                <X 
                  className="w-3 h-3 cursor-pointer hover:text-red-400" 
                  onClick={() => toggleSymbol(sym)}
                />
              </span>
            ))}
          </div>
        )}
        
        {showSymbolPicker && (
          <div className="mt-2 p-2 bg-[#0A0A0A] rounded border border-[#27272A]">
            <Input
              placeholder="Search symbols..."
              value={symbolSearch}
              onChange={(e) => setSymbolSearch(e.target.value)}
              className="h-7 text-xs bg-[#1a1a1a] border-[#27272A] mb-2"
            />
            <div className="max-h-32 overflow-auto flex flex-wrap gap-1">
              {filteredSymbols.slice(0, 50).map(sym => (
                <button
                  key={sym}
                  onClick={() => toggleSymbol(sym)}
                  className={`px-2 py-0.5 text-xs rounded ${
                    (filter.symbols || []).includes(sym)
                      ? 'bg-purple-600 text-white'
                      : 'bg-[#27272A] text-[#A1A1AA] hover:bg-[#3f3f46]'
                  }`}
                >
                  {sym}
                </button>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onChange({ ...filter, symbols: [] })}
                className="h-6 text-xs text-green-400"
              >
                All Symbols
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onChange({ ...filter, symbols: filteredSymbols.slice(0, 10) })}
                className="h-6 text-xs text-blue-400"
              >
                Select First 10
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* Conditions */}
      <div className="space-y-2">
        {filter.conditions.map((cond, idx) => (
          <div key={cond.id} className="flex items-center gap-2 text-xs">
            {idx > 0 && <span className="text-purple-400 font-bold w-8">AND</span>}
            {idx === 0 && <span className="w-8"></span>}
            
            <Select 
              value={cond.type} 
              onValueChange={(v) => {
                const condType = CONDITION_TYPES[v.toUpperCase()] || CONDITION_TYPES.BID_QTY_PERCENT;
                updateCondition(cond.id, { 
                  type: v, 
                  operator: condType.operator,
                  value: condType.defaultValue 
                });
              }}
            >
              <SelectTrigger className="w-32 h-7 text-xs bg-[#1a1a1a] border-[#27272A]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CONDITION_TYPES).map(([key, val]) => (
                  <SelectItem key={key} value={val.key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {cond.type !== 'agg_buy' && cond.type !== 'agg_sell' && (
              <>
                <span className="text-[#71717A]">
                  {cond.type === 'volume_spike' ? 'x' : '>'}
                </span>
                <Input
                  type="number"
                  value={cond.value}
                  onChange={(e) => updateCondition(cond.id, { value: parseFloat(e.target.value) || 0 })}
                  className="h-7 w-20 text-xs bg-[#1a1a1a] border-[#27272A]"
                />
                <span className="text-[#52525B]">
                  {cond.type.includes('percent') ? '%' : cond.type === 'volume_spike' ? 'avg' : ''}
                </span>
              </>
            )}
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => deleteCondition(cond.id)}
              className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>
      
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={addCondition}
        className="mt-2 text-xs text-purple-400 hover:text-purple-300"
      >
        <Plus className="w-3 h-3 mr-1" /> Add Condition
      </Button>
    </div>
  );
};

// Settings Panel Component
const SettingsPanel = ({ settings, setSettings, customFilters, setCustomFilters, onSave, saving, availableSymbols, telegramSettings, setTelegramSettings }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [testingSend, setTestingSend] = useState(false);
  
  const handleChange = (type, key, value) => {
    setSettings(prev => ({
      ...prev,
      [type]: { ...prev[type], [key]: value }
    }));
  };
  
  const addCustomFilter = () => {
    const newFilter = {
      id: Date.now().toString(),
      name: '',
      enabled: true,
      tickCount: 3,
      triggerMode: 'consecutive',
      symbols: [],
      conditions: [
        { id: '1', type: 'bid_qty_percent', value: 50, operator: '>' }
      ]
    };
    setCustomFilters(prev => [...prev, newFilter]);
  };
  
  const updateCustomFilter = (filterId, updates) => {
    setCustomFilters(prev => prev.map(f => f.id === filterId ? updates : f));
  };
  
  const deleteCustomFilter = (filterId) => {
    setCustomFilters(prev => prev.filter(f => f.id !== filterId));
  };
  
  const testTelegramAlert = async () => {
    setTestingSend(true);
    try {
      const res = await fetch(`${API_URL}/api/telegram/test`, { method: 'POST' });
      const data = await res.json();
      alert(data.status === 'sent' ? 'Test alert sent! Check your Telegram.' : 'Failed to send test alert');
    } catch (e) {
      alert('Failed to send test alert: ' + e.message);
    }
    setTestingSend(false);
  };

  return (
    <div className="bg-[#111111] border border-[#27272A] rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Settings2 className="w-5 h-5" />
            Activity Detection Settings
          </h3>
          <div className="flex bg-[#0A0A0A] rounded-lg p-1">
            <button
              onClick={() => setActiveTab('basic')}
              className={`px-3 py-1 text-xs rounded ${activeTab === 'basic' ? 'bg-blue-600 text-white' : 'text-[#71717A] hover:text-white'}`}
            >
              Basic Activities
            </button>
            <button
              onClick={() => setActiveTab('advanced')}
              className={`px-3 py-1 text-xs rounded flex items-center gap-1 ${activeTab === 'advanced' ? 'bg-purple-600 text-white' : 'text-[#71717A] hover:text-white'}`}
            >
              <Sliders className="w-3 h-3" />
              Advanced Filters
            </button>
            <button
              onClick={() => setActiveTab('telegram')}
              className={`px-3 py-1 text-xs rounded flex items-center gap-1 ${activeTab === 'telegram' ? 'bg-green-600 text-white' : 'text-[#71717A] hover:text-white'}`}
            >
              <Send className="w-3 h-3" />
              Telegram Alerts
            </button>
          </div>
        </div>
        <Button 
          onClick={onSave} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>
      
      {activeTab === 'telegram' && (
        <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Send className="w-6 h-6 text-green-400" />
              <div>
                <h4 className="text-white font-semibold">Telegram Alerts</h4>
                <p className="text-xs text-[#52525B]">Get real-time alerts for custom filter activities</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={telegramSettings.enabled || false} 
                onChange={(e) => setTelegramSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#27272A] peer-checked:bg-green-600 rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[3px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
            </label>
          </div>
          
          {telegramSettings.enabled && (
            <div className="space-y-4">
              <div className="p-3 bg-[#1a1a1a] rounded border border-green-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Bell className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-white font-medium">Alert Configuration</span>
                </div>
                <p className="text-xs text-[#71717A] mb-3">
                  When enabled, you'll receive Telegram messages for all <b className="text-purple-400">Custom Filter</b> activities.
                  Make sure your Telegram Bot Token and Chat ID are configured in the backend.
                </p>
                <Button 
                  onClick={testTelegramAlert}
                  disabled={testingSend}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {testingSend ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Test Alert
                </Button>
              </div>
              
              <div className="p-3 bg-[#1a1a1a] rounded border border-[#27272A]">
                <h5 className="text-sm text-white font-medium mb-2">Alert Format Preview</h5>
                <div className="bg-[#0A0A0A] p-3 rounded text-xs font-mono text-[#71717A] whitespace-pre-line">
{`⚡ NIFTY26FEBFUT
Big Buy Signal

💰 Price: 25,920.00
📈 LTQ: 650
📊 Volume: 12,500

⏱ Mode: Consecutive (3 ticks)

Conditions Met:
  ✓ Bid Qty > 60%
  ✓ LTQ > 500
  ✓ Volume Spike 2x

🕐 10:15:30`}
                </div>
              </div>
            </div>
          )}
          
          {!telegramSettings.enabled && (
            <div className="text-center py-6 text-[#52525B]">
              <BellOff className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>Telegram alerts are disabled</p>
              <p className="text-xs">Enable to receive real-time notifications</p>
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'basic' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Object.entries(settings).map(([type, config]) => (
            <div key={type} className="p-3 bg-[#0A0A0A] rounded border border-[#27272A]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-white">{config.label}</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={config.enabled} 
                    onChange={(e) => handleChange(type, 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-[#27272A] peer-checked:bg-blue-600 rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                </label>
              </div>
              {config.enabled && (
                <div className="space-y-2 text-xs">
                  {config.ltqThreshold !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#71717A]">LTQ Threshold</span>
                      <Input 
                        type="number" 
                        value={config.ltqThreshold} 
                        onChange={(e) => handleChange(type, 'ltqThreshold', parseInt(e.target.value) || 0)}
                        className="h-7 text-xs bg-[#1a1a1a] border-[#27272A] w-24"
                      />
                    </div>
                  )}
                  {config.consecutiveTicks !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#71717A]">Consecutive Ticks</span>
                      <Input 
                        type="number" 
                        value={config.consecutiveTicks} 
                        onChange={(e) => handleChange(type, 'consecutiveTicks', parseInt(e.target.value) || 1)}
                        className="h-7 text-xs bg-[#1a1a1a] border-[#27272A] w-24"
                      />
                    </div>
                  )}
                  {config.changePercent !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#71717A]">Change %</span>
                      <Input 
                        type="number" 
                        value={config.changePercent} 
                        onChange={(e) => handleChange(type, 'changePercent', parseInt(e.target.value) || 0)}
                        className="h-7 text-xs bg-[#1a1a1a] border-[#27272A] w-24"
                      />
                    </div>
                  )}
                  {config.streakCount !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#71717A]">Streak Count</span>
                      <Input 
                        type="number" 
                        value={config.streakCount} 
                        onChange={(e) => handleChange(type, 'streakCount', parseInt(e.target.value) || 1)}
                        className="h-7 text-xs bg-[#1a1a1a] border-[#27272A] w-24"
                      />
                    </div>
                  )}
                  {config.volumeMultiplier !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#71717A]">Volume Multiplier</span>
                      <Input 
                        type="number" 
                        step="0.1"
                        value={config.volumeMultiplier} 
                        onChange={(e) => handleChange(type, 'volumeMultiplier', parseFloat(e.target.value) || 1)}
                        className="h-7 text-xs bg-[#1a1a1a] border-[#27272A] w-24"
                      />
                    </div>
                  )}
                  {config.maxLtq !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#71717A]">Max LTQ</span>
                      <Input 
                        type="number" 
                        value={config.maxLtq} 
                        onChange={(e) => handleChange(type, 'maxLtq', parseInt(e.target.value) || 0)}
                        className="h-7 text-xs bg-[#1a1a1a] border-[#27272A] w-24"
                      />
                    </div>
                  )}
                  {config.oiChangePercent !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#71717A]">OI Change %</span>
                      <Input 
                        type="number" 
                        value={config.oiChangePercent} 
                        onChange={(e) => handleChange(type, 'oiChangePercent', parseInt(e.target.value) || 0)}
                        className="h-7 text-xs bg-[#1a1a1a] border-[#27272A] w-24"
                      />
                    </div>
                  )}
                  {config.imbalancePercent !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#71717A]">Imbalance %</span>
                      <Input 
                        type="number" 
                        value={config.imbalancePercent} 
                        onChange={(e) => handleChange(type, 'imbalancePercent', parseInt(e.target.value) || 0)}
                        className="h-7 text-xs bg-[#1a1a1a] border-[#27272A] w-24"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {activeTab === 'advanced' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-white font-medium">Custom Combined Filters</h4>
              <p className="text-xs text-[#52525B]">Create your own activity detection rules by combining multiple conditions</p>
            </div>
            <Button onClick={addCustomFilter} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-1" /> New Filter
            </Button>
          </div>
          
          {customFilters.length === 0 ? (
            <div className="text-center py-8 text-[#52525B]">
              <Sliders className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No custom filters yet</p>
              <p className="text-xs">Click "New Filter" to create combined conditions</p>
            </div>
          ) : (
            customFilters.map(filter => (
              <CustomFilterEditor
                key={filter.id}
                filter={filter}
                onChange={(updates) => updateCustomFilter(filter.id, updates)}
                onDelete={() => deleteCustomFilter(filter.id)}
                availableSymbols={availableSymbols}
              />
            ))
          )}
          
          {/* Example Filters */}
          <div className="mt-4 p-3 bg-[#1a1a1a] rounded border border-dashed border-[#27272A]">
            <p className="text-xs text-[#52525B] font-medium mb-2">Example Filter Ideas:</p>
            <ul className="text-xs text-[#52525B] space-y-1">
              <li>• <span className="text-purple-400">Big Buy Signal:</span> Bid Qty &gt; 60% + LTQ &gt; 500 + Volume Spike 2x in 3 ticks</li>
              <li>• <span className="text-purple-400">Institutional Activity:</span> LTQ &gt; 1000 + Volume Spike 3x + Aggressive Buy</li>
              <li>• <span className="text-purple-400">Sell Pressure:</span> Ask Qty &gt; 70% + Aggressive Sell + OI Change &gt; 2%</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Activity Log Page Component
const ActivityLogPage = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [liveActivities, setLiveActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [minLtqFilter, setMinLtqFilter] = useState('');
  const [minVolumeFilter, setMinVolumeFilter] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [availableDates, setAvailableDates] = useState([]);
  
  // UI State
  const [showSettings, setShowSettings] = useState(false);
  const [sortField, setSortField] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Activity detection settings
  const [activitySettings, setActivitySettings] = useState(DEFAULT_ACTIVITY_SETTINGS);
  const [customFilters, setCustomFilters] = useState(DEFAULT_CUSTOM_FILTERS);
  const [telegramSettings, setTelegramSettings] = useState({ enabled: false });
  
  // Refs for live detection
  const prevTickDataRef = useRef({});
  const aggStreaksRef = useRef({});
  const volumeHistoryRef = useRef({});
  const ltqHistoryRef = useRef({});
  const customFilterHistoryRef = useRef({}); // Track history for custom filters
  const saveQueueRef = useRef([]);
  const saveTimerRef = useRef(null);
  
  // Navigate to tick log with selected symbol
  const openTickLog = useCallback((symbol, token) => {
    // Navigate to dashboard with tick history open and symbol pre-selected
    navigate('/', { 
      state: { 
        openTickHistory: true, 
        selectedSymbol: symbol,
        selectedToken: token 
      } 
    });
  }, [navigate]);
  
  // Load settings from backend
  const loadSettings = useCallback(async () => {
    try {
      // Load activity settings
      const res = await fetch(`${API_URL}/api/activities/settings`);
      const data = await res.json();
      if (data.settings) {
        if (data.settings.basicSettings) {
          setActivitySettings(prev => ({ ...DEFAULT_ACTIVITY_SETTINGS, ...data.settings.basicSettings }));
        }
        if (data.settings.customFilters) {
          setCustomFilters(data.settings.customFilters);
        }
      }
      
      // Load telegram settings
      const telegramRes = await fetch(`${API_URL}/api/telegram/settings`);
      const telegramData = await telegramRes.json();
      if (telegramData.settings) {
        setTelegramSettings(telegramData.settings);
      }
    } catch (e) {
      console.error('Error loading settings:', e);
    }
  }, []);
  
  // Save settings to backend
  const saveSettings = useCallback(async () => {
    setSaving(true);
    try {
      // Save activity settings
      await fetch(`${API_URL}/api/activities/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          basicSettings: activitySettings,
          customFilters: customFilters
        })
      });
      
      // Save telegram settings
      await fetch(`${API_URL}/api/telegram/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(telegramSettings)
      });
      
      localStorage.setItem('activitySettings', JSON.stringify({ basicSettings: activitySettings, customFilters }));
    } catch (e) {
      console.error('Error saving settings:', e);
    }
    setSaving(false);
  }, [activitySettings, customFilters, telegramSettings]);
  
  // Load activities from backend
  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date: selectedDate, limit: '2000' });
      const res = await fetch(`${API_URL}/api/activities?${params}`);
      const data = await res.json();
      setActivities(data.activities || []);
      setTotalCount(data.total || 0);
    } catch (e) {
      console.error('Error loading activities:', e);
    }
    setLoading(false);
  }, [selectedDate]);
  
  // Load available dates
  const loadDates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/activities/dates`);
      const data = await res.json();
      setAvailableDates(data.dates || []);
    } catch (e) {
      console.error('Error loading dates:', e);
    }
  }, []);
  
  // Save activities to backend (batch)
  const saveActivitiesToBackend = useCallback(async (newActivities) => {
    if (newActivities.length === 0) return;
    try {
      await fetch(`${API_URL}/api/activities/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newActivities)
      });
    } catch (e) {
      console.error('Error saving activities:', e);
    }
  }, []);
  
  // Queue activities for batch save
  const queueActivitySave = useCallback((activity) => {
    saveQueueRef.current.push(activity);
    
    if (!saveTimerRef.current) {
      saveTimerRef.current = setTimeout(() => {
        if (saveQueueRef.current.length > 0) {
          saveActivitiesToBackend([...saveQueueRef.current]);
          saveQueueRef.current = [];
        }
        saveTimerRef.current = null;
      }, 5000);
    }
  }, [saveActivitiesToBackend]);
  
  // Clear activities for a date
  const clearActivities = useCallback(async () => {
    if (!window.confirm(`Clear all activities for ${selectedDate}?`)) return;
    try {
      await fetch(`${API_URL}/api/activities?date=${selectedDate}`, { method: 'DELETE' });
      setActivities([]);
      setTotalCount(0);
    } catch (e) {
      console.error('Error clearing activities:', e);
    }
  }, [selectedDate]);
  
  // Export activities as CSV
  const exportActivities = useCallback(() => {
    const allActivities = [...liveActivities, ...activities];
    if (allActivities.length === 0) return;
    
    const headers = ['Time', 'Symbol', 'Activity', 'Price', 'LTQ', 'Volume', 'Details'];
    const rows = allActivities.map(a => [
      a.timestamp,
      a.symbol,
      getActivityLabel(a.type, activitySettings, customFilters),
      a.details?.price || '',
      a.details?.ltq || '',
      a.details?.volume || a.details?.volumeChange || '',
      JSON.stringify(a.details)
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activities_${selectedDate}.csv`;
    a.click();
  }, [liveActivities, activities, selectedDate, activitySettings, customFilters]);
  
  // Check custom filter conditions
  const checkCustomFilterConditions = useCallback((tick, prevTick, avgVolume) => {
    const results = [];
    const symbol = tick.tradingsymbol || String(tick.instrument_token);
    
    customFilters.filter(f => f.enabled && f.conditions.length > 0).forEach(filter => {
      const token = tick.instrument_token;
      
      // Check if filter applies to this symbol (if symbols are specified)
      if (filter.symbols && filter.symbols.length > 0) {
        if (!filter.symbols.includes(symbol)) {
          return; // Skip this filter for this symbol
        }
      }
      
      // Initialize history for this filter+token
      if (!customFilterHistoryRef.current[filter.id]) {
        customFilterHistoryRef.current[filter.id] = {};
      }
      if (!customFilterHistoryRef.current[filter.id][token]) {
        customFilterHistoryRef.current[filter.id][token] = [];
      }
      
      // Check all conditions
      let allConditionsMet = true;
      const conditionResults = [];
      
      filter.conditions.forEach(cond => {
        let condMet = false;
        let condValue = null;
        
        switch (cond.type) {
          case 'bid_qty_percent': {
            const buyQty = tick.total_buy_quantity || 0;
            const sellQty = tick.total_sell_quantity || 0;
            const total = buyQty + sellQty;
            condValue = total > 0 ? (buyQty / total) * 100 : 0;
            condMet = condValue > cond.value;
            break;
          }
          case 'ask_qty_percent': {
            const buyQty = tick.total_buy_quantity || 0;
            const sellQty = tick.total_sell_quantity || 0;
            const total = buyQty + sellQty;
            condValue = total > 0 ? (sellQty / total) * 100 : 0;
            condMet = condValue > cond.value;
            break;
          }
          case 'ltq_greater':
            condValue = tick.last_traded_quantity || 0;
            condMet = condValue > cond.value;
            break;
          case 'ltq_less':
            condValue = tick.last_traded_quantity || 0;
            condMet = condValue < cond.value && condValue > 0;
            break;
          case 'volume_spike':
            condValue = avgVolume > 0 ? (tick.volume_change || 0) / avgVolume : 0;
            condMet = condValue > cond.value;
            break;
          case 'price_change': {
            const priceChange = prevTick ? Math.abs((tick.last_price - prevTick.last_price) / prevTick.last_price * 100) : 0;
            condValue = priceChange;
            condMet = priceChange > cond.value;
            break;
          }
          case 'agg_buy':
            condValue = tick.agg_buy > 0;
            condMet = condValue;
            break;
          case 'agg_sell':
            condValue = tick.agg_sell > 0;
            condMet = condValue;
            break;
          case 'oi_change':
            condValue = Math.abs(tick.oi_pct_change || 0);
            condMet = condValue > cond.value;
            break;
          default:
            condMet = false;
        }
        
        if (!condMet) allConditionsMet = false;
        conditionResults.push({ type: cond.type, met: condMet, value: condValue });
      });
      
      // Track history - store condition results for "within" mode
      customFilterHistoryRef.current[filter.id][token].push({
        allMet: allConditionsMet,
        conditionsMet: conditionResults.map(c => c.met)
      });
      
      // Keep history limited
      const maxHistory = Math.max(50, (filter.tickCount || 3) * 2);
      if (customFilterHistoryRef.current[filter.id][token].length > maxHistory) {
        customFilterHistoryRef.current[filter.id][token].shift();
      }
      
      const history = customFilterHistoryRef.current[filter.id][token];
      const reqTicks = filter.tickCount || 3;
      const triggerMode = filter.triggerMode || 'consecutive';
      
      let triggered = false;
      
      if (triggerMode === 'consecutive') {
        // Consecutive mode: ALL conditions must match in consecutive ticks
        if (history.length >= reqTicks) {
          const recentMatches = history.slice(-reqTicks);
          triggered = recentMatches.every(m => m.allMet);
        }
      } else {
        // Within mode: Each condition must be met at least once within the tick window
        if (history.length >= reqTicks) {
          const recentHistory = history.slice(-reqTicks);
          const numConditions = filter.conditions.length;
          
          // Check if each condition was met at least once in the window
          const conditionsSatisfied = new Array(numConditions).fill(false);
          recentHistory.forEach(h => {
            h.conditionsMet.forEach((met, idx) => {
              if (met) conditionsSatisfied[idx] = true;
            });
          });
          
          triggered = conditionsSatisfied.every(sat => sat);
        }
      }
      
      if (triggered) {
        // CRITICAL FIX: Only show activity if current tick values actually meet the conditions
        // This prevents showing misleading values like LTQ 65 when filter says LTQ > 200
        
        // Validate ALL numeric conditions against current tick values
        let skipTrigger = false;
        
        for (const cond of filter.conditions) {
          const condResult = conditionResults.find(cr => cr.type === cond.type);
          
          // For value-comparison conditions, current value MUST meet the threshold
          if (cond.type === 'ltq_greater') {
            const currentLtq = tick.last_traded_quantity || 0;
            if (currentLtq <= cond.value) {
              skipTrigger = true;
              break;
            }
          } else if (cond.type === 'ltq_less') {
            const currentLtq = tick.last_traded_quantity || 0;
            if (currentLtq >= cond.value || currentLtq === 0) {
              skipTrigger = true;
              break;
            }
          } else if (cond.type === 'bid_qty_percent') {
            const buyQty = tick.total_buy_quantity || 0;
            const sellQty = tick.total_sell_quantity || 0;
            const total = buyQty + sellQty;
            const bidPercent = total > 0 ? (buyQty / total) * 100 : 0;
            if (bidPercent <= cond.value) {
              skipTrigger = true;
              break;
            }
          } else if (cond.type === 'ask_qty_percent') {
            const buyQty = tick.total_buy_quantity || 0;
            const sellQty = tick.total_sell_quantity || 0;
            const total = buyQty + sellQty;
            const askPercent = total > 0 ? (sellQty / total) * 100 : 0;
            if (askPercent <= cond.value) {
              skipTrigger = true;
              break;
            }
          }
        }
        
        if (skipTrigger) {
          // Current tick doesn't meet conditions - skip this trigger
          return;
        }
        
        results.push({
          filterId: filter.id,
          filterName: filter.name,
          conditions: conditionResults,
          tickCount: reqTicks,
          triggerMode: triggerMode,
          symbols: filter.symbols,
          allConditionsCurrentlyMet: allConditionsMet
        });
        // Reset to avoid duplicate alerts
        customFilterHistoryRef.current[filter.id][token] = [{
          allMet: allConditionsMet,
          conditionsMet: conditionResults.map(c => c.met)
        }];
      }
    });
    
    return results;
  }, [customFilters]);
  
  // Initial load
  useEffect(() => {
    loadSettings();
    loadActivities();
    loadDates();
  }, [loadSettings, loadActivities, loadDates]);
  
  // Reload when date changes
  useEffect(() => {
    loadActivities();
  }, [selectedDate, loadActivities]);
  
  // Activity detection logic - MUST be defined before the useEffect that uses it
  const detectActivities = useCallback((ticks) => {
    const newActivities = [];
    const now = new Date();
    const settings = activitySettings;
    
    ticks.forEach(tick => {
      const token = tick.instrument_token;
      const prevTick = prevTickDataRef.current[token];
      const symbol = tick.tradingsymbol || token;
      
      if (!prevTick) {
        prevTickDataRef.current[token] = tick;
        ltqHistoryRef.current[token] = [];
        return;
      }
      
      // Track volume history
      if (!volumeHistoryRef.current[token]) {
        volumeHistoryRef.current[token] = [];
      }
      volumeHistoryRef.current[token].push(tick.volume || tick.volume_traded || 0);
      if (volumeHistoryRef.current[token].length > 20) {
        volumeHistoryRef.current[token].shift();
      }
      
      const avgVolume = volumeHistoryRef.current[token].reduce((a, b) => a + b, 0) / volumeHistoryRef.current[token].length;
      const currentVolChange = tick.volume_change || 0;
      const ltq = tick.last_traded_quantity || 0;
      
      // Track LTQ history
      if (!ltqHistoryRef.current[token]) {
        ltqHistoryRef.current[token] = [];
      }
      ltqHistoryRef.current[token].push(ltq);
      if (ltqHistoryRef.current[token].length > 20) {
        ltqHistoryRef.current[token].shift();
      }
      
      // Check Custom Combined Filters
      const customMatches = checkCustomFilterConditions(tick, prevTick, avgVolume);
      customMatches.forEach(match => {
        const activity = {
          id: `${token}-custom_${match.filterId}-${Date.now()}`,
          type: `custom_${match.filterId}`,
          symbol,
          token,
          timestamp: now.toISOString(),
          details: {
            filterName: match.filterName,
            consecutiveTicks: match.consecutiveTicks,
            conditions: match.conditions,
            price: tick.last_price,
            ltq,
            volume: currentVolChange,
          }
        };
        newActivities.push(activity);
        queueActivitySave(activity);
      });
      
      // HIGH LTQ STREAK Detection - Shows when LTQ is above threshold
      // If consecutiveTicks = 1, shows every trade above threshold
      if (settings[ACTIVITY_TYPES.HIGH_LTQ_STREAK]?.enabled) {
        const threshold = settings[ACTIVITY_TYPES.HIGH_LTQ_STREAK].ltqThreshold || 1000;
        const reqConsecutive = settings[ACTIVITY_TYPES.HIGH_LTQ_STREAK].consecutiveTicks || 1;
        const ltqHistory = ltqHistoryRef.current[token] || [];
        
        // For single tick detection (consecutiveTicks = 1), check current tick directly
        if (reqConsecutive <= 1) {
          if (ltq >= threshold) {
            const activity = {
              id: `${token}-ltqstreak-${Date.now()}`,
              type: ACTIVITY_TYPES.HIGH_LTQ_STREAK,
              symbol,
              token,
              timestamp: now.toISOString(),
              details: {
                consecutiveTicks: 1,
                avgLtq: ltq,
                minLtq: ltq,
                maxLtq: ltq,
                threshold,
                price: tick.last_price,
                volume: currentVolChange,
                ltq
              }
            };
            newActivities.push(activity);
            queueActivitySave(activity);
          }
        } else if (ltqHistory.length >= reqConsecutive) {
          // For streak detection, check history
          const recentLtqs = ltqHistory.slice(-reqConsecutive);
          const allAboveThreshold = recentLtqs.every(l => l >= threshold);
          
          if (allAboveThreshold) {
            const avgLtq = recentLtqs.reduce((a, b) => a + b, 0) / recentLtqs.length;
            const activity = {
              id: `${token}-ltqstreak-${Date.now()}`,
              type: ACTIVITY_TYPES.HIGH_LTQ_STREAK,
              symbol,
              token,
              timestamp: now.toISOString(),
              details: {
                consecutiveTicks: reqConsecutive,
                avgLtq: Math.round(avgLtq),
                minLtq: Math.min(...recentLtqs),
                maxLtq: Math.max(...recentLtqs),
                threshold,
                price: tick.last_price,
                volume: currentVolChange,
                ltq
              }
            };
            newActivities.push(activity);
            queueActivitySave(activity);
            ltqHistoryRef.current[token] = [ltq]; // Reset after trigger
          }
        }
      }
      
      // Large Trade Detection
      if (settings[ACTIVITY_TYPES.LARGE_TRADE]?.enabled && ltq >= settings[ACTIVITY_TYPES.LARGE_TRADE].ltqThreshold) {
        const activity = {
          id: `${token}-largetrade-${Date.now()}`,
          type: ACTIVITY_TYPES.LARGE_TRADE,
          symbol,
          token,
          timestamp: now.toISOString(),
          details: {
            ltq,
            price: tick.last_price,
            tradeType: tick.agg_buy > 0 ? 'BUY' : tick.agg_sell > 0 ? 'SELL' : 'NEUTRAL',
            volume: currentVolChange,
          }
        };
        newActivities.push(activity);
        queueActivitySave(activity);
      }
      
      // Aggressive Buy/Sell Streaks
      if (!aggStreaksRef.current[token]) {
        aggStreaksRef.current[token] = { buyStreak: 0, sellStreak: 0 };
      }
      
      if (tick.agg_buy > 0) {
        aggStreaksRef.current[token].buyStreak++;
        aggStreaksRef.current[token].sellStreak = 0;
        
        const reqStreak = settings[ACTIVITY_TYPES.AGGRESSIVE_BUY_STREAK]?.streakCount || 4;
        if (settings[ACTIVITY_TYPES.AGGRESSIVE_BUY_STREAK]?.enabled && aggStreaksRef.current[token].buyStreak >= reqStreak) {
          const activity = {
            id: `${token}-aggbuy-${Date.now()}`,
            type: ACTIVITY_TYPES.AGGRESSIVE_BUY_STREAK,
            symbol,
            token,
            timestamp: now.toISOString(),
            details: {
              streak: aggStreaksRef.current[token].buyStreak,
              ltq,
              totalVolume: tick.agg_buy,
              price: tick.last_price,
              volume: currentVolChange,
            }
          };
          newActivities.push(activity);
          queueActivitySave(activity);
        }
      } else if (tick.agg_sell > 0) {
        aggStreaksRef.current[token].sellStreak++;
        aggStreaksRef.current[token].buyStreak = 0;
        
        const reqStreak = settings[ACTIVITY_TYPES.AGGRESSIVE_SELL_STREAK]?.streakCount || 4;
        if (settings[ACTIVITY_TYPES.AGGRESSIVE_SELL_STREAK]?.enabled && aggStreaksRef.current[token].sellStreak >= reqStreak) {
          const activity = {
            id: `${token}-aggsell-${Date.now()}`,
            type: ACTIVITY_TYPES.AGGRESSIVE_SELL_STREAK,
            symbol,
            token,
            timestamp: now.toISOString(),
            details: {
              streak: aggStreaksRef.current[token].sellStreak,
              ltq,
              totalVolume: tick.agg_sell,
              price: tick.last_price,
              volume: currentVolChange,
            }
          };
          newActivities.push(activity);
          queueActivitySave(activity);
        }
      } else {
        aggStreaksRef.current[token].buyStreak = 0;
        aggStreaksRef.current[token].sellStreak = 0;
      }
      
      // Volume Spike
      if (settings[ACTIVITY_TYPES.VOLUME_SPIKE]?.enabled && currentVolChange > avgVolume * settings[ACTIVITY_TYPES.VOLUME_SPIKE].volumeMultiplier && avgVolume > 0) {
        const activity = {
          id: `${token}-volspike-${Date.now()}`,
          type: ACTIVITY_TYPES.VOLUME_SPIKE,
          symbol,
          token,
          timestamp: now.toISOString(),
          details: {
            volumeChange: currentVolChange,
            avgVolume: avgVolume.toFixed(0),
            multiplier: (currentVolChange / avgVolume).toFixed(1),
            price: tick.last_price,
            ltq,
            volume: currentVolChange,
          }
        };
        newActivities.push(activity);
        queueActivitySave(activity);
      }
      
      prevTickDataRef.current[token] = tick;
    });
    
    if (newActivities.length > 0) {
      setLiveActivities(prev => [...newActivities, ...prev].slice(0, 500));
    }
  }, [activitySettings, checkCustomFilterConditions, queueActivitySave]);
  
  // Live activity detection from tick data - High frequency with CPU optimization
  // Uses requestAnimationFrame + setInterval combo for smooth 50ms updates without blocking UI
  const rafIdRef = useRef(null);
  const lastTickTimestampRef = useRef({});
  const pendingTicksRef = useRef([]);
  
  useEffect(() => {
    let isMounted = true;
    let lastFetchTime = 0;
    const POLL_INTERVAL = 50; // 50ms polling
    
    // Efficient polling with request batching
    const pollTicks = async () => {
      if (!isMounted) return;
      
      const now = Date.now();
      if (now - lastFetchTime < POLL_INTERVAL) {
        rafIdRef.current = requestAnimationFrame(pollTicks);
        return;
      }
      lastFetchTime = now;
      
      try {
        const res = await fetch(`${API_URL}/api/ticks`);
        const data = await res.json();
        
        if (data.ticks && data.ticks.length > 0) {
          // Filter only new ticks (not already processed)
          const newTicks = data.ticks.filter(tick => {
            const token = tick.instrument_token;
            const lastTimestamp = lastTickTimestampRef.current[token] || 0;
            const tickTimestamp = tick.timestamp_ms || 0;
            
            if (tickTimestamp > lastTimestamp) {
              lastTickTimestampRef.current[token] = tickTimestamp;
              return true;
            }
            return false;
          });
          
          if (newTicks.length > 0) {
            // Batch process in requestAnimationFrame to avoid blocking
            requestAnimationFrame(() => {
              detectActivities(newTicks);
            });
          }
        }
      } catch (e) {
        // Silently fail for polling
      }
      
      // Continue polling
      if (isMounted) {
        rafIdRef.current = requestAnimationFrame(pollTicks);
      }
    };
    
    // Start polling
    rafIdRef.current = requestAnimationFrame(pollTicks);
    
    return () => {
      isMounted = false;
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [activitySettings, customFilters, detectActivities]);
  
  // Combine live and historical activities
  const allActivities = useMemo(() => {
    return [...liveActivities, ...activities];
  }, [liveActivities, activities]);
  
  // Get unique symbols for filter picker
  const availableSymbols = useMemo(() => {
    const symbols = new Set(allActivities.map(a => a.symbol));
    return Array.from(symbols).sort();
  }, [allActivities]);
  
  // Filtered and sorted activities
  const filteredActivities = useMemo(() => {
    let filtered = allActivities;
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(a => a.type.includes(typeFilter));
    }
    if (symbolFilter) {
      filtered = filtered.filter(a => a.symbol.toLowerCase().includes(symbolFilter.toLowerCase()));
    }
    if (minLtqFilter && !isNaN(parseInt(minLtqFilter))) {
      filtered = filtered.filter(a => (a.details?.ltq || 0) >= parseInt(minLtqFilter));
    }
    if (minVolumeFilter && !isNaN(parseInt(minVolumeFilter))) {
      filtered = filtered.filter(a => (a.details?.volume || a.details?.volumeChange || 0) >= parseInt(minVolumeFilter));
    }
    
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'symbol':
          return sortOrder === 'asc' ? a.symbol.localeCompare(b.symbol) : b.symbol.localeCompare(a.symbol);
        case 'ltq':
          aVal = a.details?.ltq || 0;
          bVal = b.details?.ltq || 0;
          break;
        case 'volume':
          aVal = a.details?.volume || a.details?.volumeChange || 0;
          bVal = b.details?.volume || b.details?.volumeChange || 0;
          break;
        case 'price':
          aVal = a.details?.price || 0;
          bVal = b.details?.price || 0;
          break;
        default:
          aVal = new Date(a.timestamp).getTime();
          bVal = new Date(b.timestamp).getTime();
      }
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return filtered;
  }, [allActivities, typeFilter, symbolFilter, minLtqFilter, minVolumeFilter, sortField, sortOrder]);
  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };
  
  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };
  
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <div className="bg-[#111111] border-b border-[#27272A] sticky top-0 z-40">
        <div className="max-w-[1920px] mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/')}
                className="text-[#71717A] hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div className="flex items-center gap-2">
                <Activity className="w-6 h-6 text-blue-400" />
                <h1 className="text-xl font-bold">Activity Log</h1>
                <span className="text-sm text-[#52525B]">
                  ({filteredActivities.length} / {allActivities.length} activities)
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Date Selector */}
              <div className="flex items-center gap-2 mr-4">
                <Calendar className="w-4 h-4 text-[#71717A]" />
                <Select value={selectedDate} onValueChange={setSelectedDate}>
                  <SelectTrigger className="w-40 h-8 text-sm bg-[#0A0A0A] border-[#27272A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={new Date().toISOString().split('T')[0]}>Today</SelectItem>
                    {availableDates.filter(d => d !== new Date().toISOString().split('T')[0]).map(date => (
                      <SelectItem key={date} value={date}>{date}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                variant={showSettings ? "default" : "outline"} 
                size="sm"
                onClick={() => setShowSettings(!showSettings)}
                className={showSettings ? "bg-blue-600" : ""}
              >
                <Settings2 className="w-4 h-4 mr-1" />
                Settings
              </Button>
              
              <Button variant="outline" size="sm" onClick={exportActivities}>
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
              
              <Button variant="outline" size="sm" onClick={loadActivities}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
              
              <Button variant="outline" size="sm" onClick={clearActivities} className="text-red-400 hover:text-red-300">
                <Trash2 className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-[1920px] mx-auto p-4">
        {/* Settings Panel */}
        {showSettings && (
          <SettingsPanel 
            settings={activitySettings} 
            setSettings={setActivitySettings}
            customFilters={customFilters}
            setCustomFilters={setCustomFilters}
            onSave={saveSettings}
            saving={saving}
            availableSymbols={availableSymbols}
            telegramSettings={telegramSettings}
            setTelegramSettings={setTelegramSettings}
          />
        )}
        
        {/* Filters */}
        <div className="flex items-center gap-3 p-3 bg-[#111111] border border-[#27272A] rounded-lg mb-4 flex-wrap">
          <Filter className="w-4 h-4 text-[#52525B]" />
          <span className="text-xs text-[#52525B] font-medium">Filters:</span>
          
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-44 h-8 text-xs bg-[#0A0A0A] border-[#27272A]">
              <SelectValue placeholder="Activity Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="custom">Custom Filters</SelectItem>
              <SelectItem value="high_ltq_streak">High LTQ Streak</SelectItem>
              <SelectItem value="large_trade">Large Trade</SelectItem>
              <SelectItem value="buy">Buy Activities</SelectItem>
              <SelectItem value="sell">Sell Activities</SelectItem>
              <SelectItem value="aggressive">Aggressive</SelectItem>
              <SelectItem value="institutional">Institutional</SelectItem>
              <SelectItem value="iceberg">Iceberg Orders</SelectItem>
              <SelectItem value="spike">Volume/OI Spike</SelectItem>
              <SelectItem value="imbalance">Bid/Ask Imbalance</SelectItem>
            </SelectContent>
          </Select>
          
          <Input 
            placeholder="Filter by Symbol..." 
            value={symbolFilter}
            onChange={(e) => setSymbolFilter(e.target.value)}
            className="w-36 h-8 text-xs bg-[#0A0A0A] border-[#27272A]"
          />
          
          <Input 
            placeholder="Min LTQ..." 
            value={minLtqFilter}
            onChange={(e) => setMinLtqFilter(e.target.value)}
            className="w-28 h-8 text-xs bg-[#0A0A0A] border-[#27272A]"
            type="number"
          />
          
          <Input 
            placeholder="Min Volume..." 
            value={minVolumeFilter}
            onChange={(e) => setMinVolumeFilter(e.target.value)}
            className="w-28 h-8 text-xs bg-[#0A0A0A] border-[#27272A]"
            type="number"
          />
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setTypeFilter('all'); setSymbolFilter(''); setMinLtqFilter(''); setMinVolumeFilter(''); }}
            className="text-xs text-red-400 hover:text-red-300"
          >
            Clear Filters
          </Button>
          
          <div className="ml-auto flex items-center gap-2 text-xs text-[#52525B]">
            <Database className="w-3 h-3" />
            <span>Live: {liveActivities.length} | Stored: {totalCount}</span>
          </div>
        </div>
        
        {/* Activities Table */}
        <div className="bg-[#111111] border border-[#27272A] rounded-lg overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#0A0A0A] border-b border-[#27272A] text-xs font-semibold text-[#71717A] uppercase">
            <div className="col-span-1 cursor-pointer hover:text-white flex items-center gap-1" onClick={() => handleSort('timestamp')}>
              Time <SortIcon field="timestamp" />
            </div>
            <div className="col-span-2 cursor-pointer hover:text-white flex items-center gap-1" onClick={() => handleSort('symbol')}>
              Symbol <SortIcon field="symbol" />
            </div>
            <div className="col-span-2">Activity</div>
            <div className="col-span-1 cursor-pointer hover:text-white flex items-center gap-1" onClick={() => handleSort('price')}>
              Price <SortIcon field="price" />
            </div>
            <div className="col-span-1 cursor-pointer hover:text-white flex items-center gap-1" onClick={() => handleSort('ltq')}>
              LTQ <SortIcon field="ltq" />
            </div>
            <div className="col-span-1 cursor-pointer hover:text-white flex items-center gap-1" onClick={() => handleSort('volume')}>
              Volume <SortIcon field="volume" />
            </div>
            <div className="col-span-4">Details</div>
          </div>
          
          {/* Table Body */}
          <div className="max-h-[calc(100vh-320px)] overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
              </div>
            ) : filteredActivities.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-[#52525B]">
                <Activity className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-lg">No activities found</p>
                <p className="text-sm">Adjust filters or wait for market activity</p>
              </div>
            ) : (
              <div className="divide-y divide-[#1a1a1a]">
                {filteredActivities.map((activity, idx) => (
                  <div 
                    key={activity.id || idx} 
                    className={`grid grid-cols-12 gap-2 px-4 py-3 text-sm hover:bg-[#1a1a1a] border-l-4 ${getActivityColor(activity.type)}`}
                  >
                    <div className="col-span-1 text-[#71717A] flex items-center text-xs">
                      <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                      {formatTime(activity.timestamp)}
                    </div>
                    <div className="col-span-2 font-mono text-white font-semibold flex items-center gap-2">
                      <ActivityIcon type={activity.type} />
                      <button 
                        onClick={() => openTickLog(activity.symbol, activity.token)}
                        className="hover:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer"
                        title="Open Tick Log for this symbol"
                      >
                        {activity.symbol}
                        <ExternalLink className="w-3 h-3 opacity-50" />
                      </button>
                    </div>
                    <div className="col-span-2">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${activity.type.includes('custom') ? 'bg-purple-600/30 text-purple-300' : 'bg-[#27272A] text-white'}`}>
                        {getActivityLabel(activity.type, activitySettings, customFilters)}
                      </span>
                    </div>
                    <div className="col-span-1 text-white font-mono">
                      {activity.details?.price?.toFixed(2) || '-'}
                    </div>
                    <div className="col-span-1 text-blue-400 font-mono font-semibold">
                      {activity.details?.ltq || '-'}
                    </div>
                    <div className="col-span-1 text-purple-400 font-mono">
                      {formatVolume(activity.details?.volume || activity.details?.volumeChange)}
                    </div>
                    <div className="col-span-4 text-xs text-[#A1A1AA] flex flex-wrap gap-2">
                      {activity.details?.streak && (
                        <span className="text-yellow-400 font-medium">Streak: {activity.details.streak}</span>
                      )}
                      {activity.details?.consecutiveTicks && !activity.details?.conditions && (
                        <span className="text-purple-400 font-medium">{activity.details.consecutiveTicks} ticks | Avg LTQ: {activity.details.avgLtq}</span>
                      )}
                      {activity.details?.conditions && (
                        <span className="text-purple-400 font-medium">
                          {activity.details.consecutiveTicks} ticks | 
                          {activity.details.conditions.filter(c => c.met).map(c => c.type.replace(/_/g, ' ')).join(' + ')}
                        </span>
                      )}
                      {activity.details?.changePct && (
                        <span className="text-cyan-400">Change: {activity.details.changePct}%</span>
                      )}
                      {activity.details?.multiplier && (
                        <span className="text-orange-400">{activity.details.multiplier}x avg vol</span>
                      )}
                      {activity.details?.tradeType && (
                        <span className={activity.details.tradeType === 'BUY' ? 'text-green-400 font-medium' : activity.details.tradeType === 'SELL' ? 'text-red-400 font-medium' : 'text-gray-400'}>
                          {activity.details.tradeType}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Footer Legend */}
        <div className="mt-4 p-3 bg-[#111111] border border-[#27272A] rounded-lg flex flex-wrap gap-6 text-xs text-[#71717A]">
          <span className="font-medium text-white">Legend:</span>
          <span className="flex items-center gap-1"><Zap className="w-4 h-4 text-purple-400" /> Custom Filter / High LTQ</span>
          <span className="flex items-center gap-1"><TrendingUp className="w-4 h-4 text-green-400" /> Buy / Aggressive Buy</span>
          <span className="flex items-center gap-1"><TrendingDown className="w-4 h-4 text-red-400" /> Sell / Aggressive Sell</span>
          <span className="flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-yellow-400" /> Iceberg Order</span>
          <span className="flex items-center gap-1"><Activity className="w-4 h-4 text-blue-400" /> Volume/OI Spike</span>
          <span className="ml-auto text-[#52525B]">Click on symbol to open Tick-by-Tick Log</span>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogPage;
